import pick from 'lodash/pick';
import moment from 'moment';
import { addMarketplaceEntities, getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { fetchCurrentUser, fetchCurrentUserHasOrdersSuccess } from '../../ducks/user.duck';
import { updateUserProfile } from '../../extensions/profile-slug/utils/api';
import { CUSTOM_SCHEDULE } from '../../extensions/edit-listing/config/listing';
import { getSession, getEndDate } from '../../extensions/listing-page/utils/listing.utils';
import { initPasscodeValidationState } from '../../extensions/private-listings/ducks/privateListings.duck';
import {
  BLOCK_BOOKING_ALIAS,
  BOOKING_CLASS_LISTING,
} from '../../extensions/session-details/config/listings';
import { speculateTransaction } from '../../extensions/transaction-update/utils/api';
import { getProcess, isBookingProcessAlias } from '../../transactions/transaction';
import { getIdBySlug, transactionLineItems } from '../../util/api';
import { denormalisedResponseEntities } from '../../util/data';
import {
  bookingTimeUnits,
  findNextBoundary,
  getStartOf,
  monthIdString,
  stringifyDateToISO8601,
} from '../../util/dates';
import { storableError } from '../../util/errors';
import * as log from '../../util/log';
import { createImageVariantConfig, types as sdkTypes } from '../../util/sdkLoader';
import {
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
} from '../../util/urlHelpers';
import {
  hasPermissionToInitiateTransactions,
  hasPermissionToViewData,
  isUserAuthorized,
} from '../../util/userHelpers';
import { splitMergedTimeSlot } from '../../extensions/listing-page/utils/listing.utils/splitMergedTimeSlot';
import {
  isFreeBlockBookingListing,
  isFreeListing,
} from '../../extensions/free-booking/utils/listings';
import { determineTxProcessAlias } from '../../extensions/checkout-page/utils/determineTxProcessAlias';
import { clearWaitlistState } from '../../extensions/waitlist/ducks/waitlist.duck';
import { END_OPTION_SPECIFIC_DATE } from '../../extensions/edit-listing/common/config/sessions';
import listingsApi from '../../extensions/own-listings/utils/listingsApi';

const { UUID } = sdkTypes;
const MINUTE_IN_MS = 1000 * 60;
const MAX_DAYS_PER_QUERY = 90;
const MAX_TOTAL_DAYS = 366;

// Day-based time slots queries are cached for 1 minute.
const removeOutdatedDateData = (timeSlotsForDate) => {
  const now = new Date().getTime();
  const minuteAgo = now - MINUTE_IN_MS;
  return Object.fromEntries(
    Object.entries(timeSlotsForDate).filter(([dateId, data]) => {
      return data.fetchedAt && data.fetchedAt > minuteAgo;
    })
  );
};

// ================ Action types ================ //

export const SET_INITIAL_VALUES = 'app/ListingPage/SET_INITIAL_VALUES';

export const SHOW_LISTING_REQUEST = 'app/ListingPage/SHOW_LISTING_REQUEST';
export const SHOW_LISTINGS_SUCCESS = 'app/ListingPage/SHOW_LISTINGS_SUCCESS';
export const SHOW_LISTING_ERROR = 'app/ListingPage/SHOW_LISTING_ERROR';

export const FETCH_REVIEWS_REQUEST = 'app/ListingPage/FETCH_REVIEWS_REQUEST';
export const FETCH_REVIEWS_SUCCESS = 'app/ListingPage/FETCH_REVIEWS_SUCCESS';
export const FETCH_REVIEWS_ERROR = 'app/ListingPage/FETCH_REVIEWS_ERROR';

export const FETCH_MONTHLY_TIME_SLOTS_REQUEST = 'app/ListingPage/FETCH_MONTHLY_TIME_SLOTS_REQUEST';
export const FETCH_MONTHLY_TIME_SLOTS_SUCCESS = 'app/ListingPage/FETCH_MONTHLY_TIME_SLOTS_SUCCESS';
export const FETCH_MONTHLY_TIME_SLOTS_ERROR = 'app/ListingPage/FETCH_MONTHLY_TIME_SLOTS_ERROR';

export const FETCH_TIME_SLOTS_FOR_DATE_REQUEST =
  'app/ListingPage/FETCH_TIME_SLOTS_FOR_DATE_REQUEST';
export const FETCH_TIME_SLOTS_FOR_DATE_SUCCESS =
  'app/ListingPage/FETCH_TIME_SLOTS_FOR_DATE_SUCCESS';
export const FETCH_TIME_SLOTS_FOR_DATE_ERROR = 'app/ListingPage/FETCH_TIME_SLOTS_FOR_DATE_ERROR';

export const FETCH_LINE_ITEMS_REQUEST = 'app/ListingPage/FETCH_LINE_ITEMS_REQUEST';
export const FETCH_LINE_ITEMS_SUCCESS = 'app/ListingPage/FETCH_LINE_ITEMS_SUCCESS';
export const FETCH_LINE_ITEMS_ERROR = 'app/ListingPage/FETCH_LINE_ITEMS_ERROR';

export const SEND_INQUIRY_REQUEST = 'app/ListingPage/SEND_INQUIRY_REQUEST';
export const SEND_INQUIRY_SUCCESS = 'app/ListingPage/SEND_INQUIRY_SUCCESS';
export const SEND_INQUIRY_ERROR = 'app/ListingPage/SEND_INQUIRY_ERROR';

export const SPECULATE_TRANSACTION_REQUEST = 'app/ListingPage/SPECULATE_TRANSACTION_REQUEST';
export const SPECULATE_TRANSACTION_SUCCESS = 'app/ListingPage/SPECULATE_TRANSACTION_SUCCESS';
export const SPECULATE_TRANSACTION_ERROR = 'app/ListingPage/SPECULATE_TRANSACTION_ERROR';

export const FETCH_TRANSACTIONS_REQUEST = 'app/ListingPage/FETCH_TRANSACTIONS_REQUEST';
export const FETCH_TRANSACTIONS_SUCCESS = 'app/ListingPage/FETCH_TRANSACTIONS_SUCCESS';
export const FETCH_TRANSACTIONS_ERROR = 'app/ListingPage/FETCH_TRANSACTIONS_ERROR';

const FETCH_TIME_SLOTS_SUCCESS = 'app/ListingPage/FETCH_TIME_SLOTS_SUCCESS';

export const SET_REDIRECT_SLUG = 'app/ListingPage/SET_REDIRECT_SLUG';

export const MAKE_LISTING_LIVE_REQUEST = 'app/ListingPage/MAKE_LISTING_LIVE_REQUEST';
export const MAKE_LISTING_LIVE_SUCCESS = 'app/ListingPage/MAKE_LISTING_LIVE_SUCCESS';
export const MAKE_LISTING_LIVE_ERROR = 'app/ListingPage/MAKE_LISTING_LIVE_ERROR';

// ================ Reducer ================ //

const initialState = {
  id: null,
  showListingError: null,
  reviews: [],
  fetchReviewsError: null,
  monthlyTimeSlots: {
    // '2022-03': {
    //   timeSlots: [],
    //   fetchTimeSlotsError: null,
    //   fetchTimeSlotsInProgress: null,
    // },
  },
  timeSlotsForDate: {
    // For small time units, we fetch monthly time slots with sparse mode for calendar view
    // and when the user clicks on a day, we make a full time slot query. This is for that purpose.
    // '2025-02-03': {
    //   timeSlots: [],
    //   fetchedAt: 1738569600000,
    //   fetchTimeSlotsError: null,
    //   fetchTimeSlotsInProgress: null,
    // },
  },
  lineItems: null,
  fetchLineItemsInProgress: false,
  fetchLineItemsError: null,
  sendInquiryInProgress: false,
  sendInquiryError: null,
  inquiryModalOpenForListingId: null,
  speculateTransactionInProgress: false,
  speculateTransactionError: null,
  fetchTransactionInProgress: false,
  fetchTransactionInProgressError: null,
  groupedTransactionsByDate: null,
  hasFetchedTimeSlots: false,
  redirectToSlug: null,
  makeListingLiveInProgress: false,
  makeListingLiveError: null,
};

const listingPageReducer = (state = initialState, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case SET_INITIAL_VALUES:
      return { ...initialState, ...payload };

    case SHOW_LISTING_REQUEST:
      return { ...state, id: payload.id, showListingError: null };
    case SHOW_LISTINGS_SUCCESS:
      return { ...state, id: payload.id, showListingError: null };
    case SHOW_LISTING_ERROR:
      return { ...state, showListingError: payload };

    case FETCH_REVIEWS_REQUEST:
      return { ...state, fetchReviewsError: null };
    case FETCH_REVIEWS_SUCCESS:
      return { ...state, reviews: payload };
    case FETCH_REVIEWS_ERROR:
      return { ...state, fetchReviewsError: payload };

    case FETCH_MONTHLY_TIME_SLOTS_REQUEST: {
      const monthlyTimeSlots = {
        ...state.monthlyTimeSlots,
        [payload]: {
          ...state.monthlyTimeSlots[payload],
          fetchTimeSlotsError: null,
          fetchTimeSlotsInProgress: true,
        },
      };
      return { ...state, monthlyTimeSlots };
    }
    case FETCH_MONTHLY_TIME_SLOTS_SUCCESS: {
      const { monthId } = payload;
      const monthlyTimeSlots = {
        ...state.monthlyTimeSlots,
        [monthId]: {
          ...state.monthlyTimeSlots[monthId],
          fetchTimeSlotsInProgress: false,
          timeSlots: payload.timeSlots,
        },
      };
      return { ...state, monthlyTimeSlots };
    }
    case FETCH_MONTHLY_TIME_SLOTS_ERROR: {
      const { monthId } = payload;
      const monthlyTimeSlots = {
        ...state.monthlyTimeSlots,
        [monthId]: {
          ...state.monthlyTimeSlots[monthId],
          fetchTimeSlotsInProgress: false,
          fetchTimeSlotsError: payload.error,
        },
      };
      return { ...state, monthlyTimeSlots };
    }
    case FETCH_TIME_SLOTS_FOR_DATE_REQUEST: {
      const timeSlotsForDate = {
        ...removeOutdatedDateData(state.timeSlotsForDate),
        [payload]: {
          ...state.timeSlotsForDate[payload],
          fetchTimeSlotsError: null,
          fetchedAt: null,
          fetchTimeSlotsInProgress: true,
          timeSlots: [],
        },
      };
      return { ...state, timeSlotsForDate };
    }
    case FETCH_TIME_SLOTS_FOR_DATE_SUCCESS: {
      const { dateId } = payload;
      const timeSlotsForDate = {
        ...state.timeSlotsForDate,
        [dateId]: {
          ...state.timeSlotsForDate[dateId],
          fetchTimeSlotsInProgress: false,
          fetchedAt: new Date().getTime(),
          timeSlots: payload.timeSlots,
        },
      };
      return { ...state, timeSlotsForDate };
    }
    case FETCH_TIME_SLOTS_FOR_DATE_ERROR: {
      const { dateId } = payload;
      const timeSlotsForDate = {
        ...state.timeSlotsForDate,
        [dateId]: {
          ...state.timeSlotsForDate[dateId],
          fetchTimeSlotsInProgress: false,
          fetchTimeSlotsError: payload.error,
        },
      };
      return { ...state, timeSlotsForDate };
    }

    case FETCH_LINE_ITEMS_REQUEST:
      return {
        ...state,
        fetchLineItemsInProgress: true,
        fetchLineItemsError: null,
        speculateTransactionError: null, // Reset any previous speculation errors because the new item would meant the user has selected a different item
      };
    case FETCH_LINE_ITEMS_SUCCESS:
      return { ...state, fetchLineItemsInProgress: false, lineItems: payload };
    case FETCH_LINE_ITEMS_ERROR:
      return { ...state, fetchLineItemsInProgress: false, fetchLineItemsError: payload };

    case SEND_INQUIRY_REQUEST:
      return { ...state, sendInquiryInProgress: true, sendInquiryError: null };
    case SEND_INQUIRY_SUCCESS:
      return { ...state, sendInquiryInProgress: false, inquiryModalOpenForListingId: null };
    case SEND_INQUIRY_ERROR:
      return { ...state, sendInquiryInProgress: false, sendInquiryError: payload };

    case SPECULATE_TRANSACTION_REQUEST:
      return { ...state, speculateTransactionInProgress: true, speculateTransactionError: null };
    case SPECULATE_TRANSACTION_SUCCESS:
      return { ...state, speculateTransactionInProgress: false };
    case SPECULATE_TRANSACTION_ERROR:
      return {
        ...state,
        speculateTransactionInProgress: false,
        speculateTransactionError: payload,
      };
    case FETCH_TRANSACTIONS_REQUEST:
      return { ...state, fetchTransactionInProgress: true, fetchTransactionInProgressError: null };
    case FETCH_TRANSACTIONS_SUCCESS:
      return { ...state, fetchTransactionInProgress: false, groupedTransactionsByDate: payload };
    case FETCH_TRANSACTIONS_ERROR:
      return {
        ...state,
        fetchTransactionInProgress: false,
        fetchTransactionInProgressError: payload,
      };
    case FETCH_TIME_SLOTS_SUCCESS:
      return { ...state, hasFetchedTimeSlots: true };

    case SET_REDIRECT_SLUG:
      return { ...state, redirectToSlug: payload };

    case MAKE_LISTING_LIVE_REQUEST:
      return { ...state, makeListingLiveInProgress: true, makeListingLiveError: null };
    case MAKE_LISTING_LIVE_SUCCESS:
      return { ...state, makeListingLiveInProgress: false };
    case MAKE_LISTING_LIVE_ERROR:
      return { ...state, makeListingLiveInProgress: false, makeListingLiveError: payload };

    default:
      return state;
  }
};

export default listingPageReducer;

// ================ Action creators ================ //

export const setInitialValues = (initialValues) => ({
  type: SET_INITIAL_VALUES,
  payload: pick(initialValues, Object.keys(initialState)),
});

export const showListingRequest = (id) => ({
  type: SHOW_LISTING_REQUEST,
  payload: { id },
});

export const showListingSuccess = (id) => ({
  type: SHOW_LISTINGS_SUCCESS,
  payload: { id },
});

export const showListingError = (e) => ({
  type: SHOW_LISTING_ERROR,
  error: true,
  payload: e,
});

export const fetchReviewsRequest = () => ({ type: FETCH_REVIEWS_REQUEST });
export const fetchReviewsSuccess = (reviews) => ({ type: FETCH_REVIEWS_SUCCESS, payload: reviews });
export const fetchReviewsError = (error) => ({
  type: FETCH_REVIEWS_ERROR,
  error: true,
  payload: error,
});

export const fetchMonthlyTimeSlotsRequest = (monthId) => ({
  type: FETCH_MONTHLY_TIME_SLOTS_REQUEST,
  payload: monthId,
});
export const fetchMonthlyTimeSlotsSuccess = (monthId, timeSlots) => ({
  type: FETCH_MONTHLY_TIME_SLOTS_SUCCESS,
  payload: { timeSlots, monthId },
});
export const fetchMonthlyTimeSlotsError = (monthId, error) => ({
  type: FETCH_MONTHLY_TIME_SLOTS_ERROR,
  error: true,
  payload: { monthId, error },
});

export const fetchTimeSlotsForDateRequest = (dateId) => ({
  type: FETCH_TIME_SLOTS_FOR_DATE_REQUEST,
  payload: dateId,
});
export const fetchTimeSlotsForDateSuccess = (dateId, timeSlots) => ({
  type: FETCH_TIME_SLOTS_FOR_DATE_SUCCESS,
  payload: { timeSlots, dateId },
});
export const fetchTimeSlotsForDateError = (dateId, error) => ({
  type: FETCH_TIME_SLOTS_FOR_DATE_ERROR,
  error: true,
  payload: { dateId, error },
});

export const fetchLineItemsRequest = () => ({ type: FETCH_LINE_ITEMS_REQUEST });
export const fetchLineItemsSuccess = (lineItems) => ({
  type: FETCH_LINE_ITEMS_SUCCESS,
  payload: lineItems,
});
export const fetchLineItemsError = (error) => ({
  type: FETCH_LINE_ITEMS_ERROR,
  error: true,
  payload: error,
});

export const sendInquiryRequest = () => ({ type: SEND_INQUIRY_REQUEST });
export const sendInquirySuccess = () => ({ type: SEND_INQUIRY_SUCCESS });
export const sendInquiryError = (e) => ({ type: SEND_INQUIRY_ERROR, error: true, payload: e });

export const speculateTransactionRequest = () => ({
  type: SPECULATE_TRANSACTION_REQUEST,
});
export const speculateTransactionSuccess = () => ({
  type: SPECULATE_TRANSACTION_SUCCESS,
});
export const speculateTransactionError = (e) => ({
  type: SPECULATE_TRANSACTION_ERROR,
  error: true,
  payload: e,
});

export const fetchOwnTransactionsRequest = () => ({
  type: FETCH_TRANSACTIONS_REQUEST,
});
export const fetchOwnTransactionsSuccess = (groupedTransactionsByDate) => ({
  type: FETCH_TRANSACTIONS_SUCCESS,
  payload: groupedTransactionsByDate,
});
export const fetchOwnTransactionsError = (e) => ({
  type: FETCH_TRANSACTIONS_ERROR,
  error: true,
  payload: e,
});

export const fetchTimeSlotsSuccess = () => ({ type: FETCH_TIME_SLOTS_SUCCESS });

export const setRedirectSlug = (slug) => ({
  type: SET_REDIRECT_SLUG,
  payload: slug,
});

export const makeListingLiveRequest = () => ({ type: MAKE_LISTING_LIVE_REQUEST });
export const makeListingLiveSuccess = () => ({ type: MAKE_LISTING_LIVE_SUCCESS });
export const makeListingLiveError = (e) => ({
  type: MAKE_LISTING_LIVE_ERROR,
  error: true,
  payload: e,
});

// ================ Thunks ================ //

const groupTransactionsByDate = (transactions) => {
  return transactions.reduce((groupedTransactions, transaction) => {
    const { booking } = transaction;
    if (!booking) {
      return groupedTransactions;
    }
    const {
      attributes: { start: bookingStart, seats },
    } = booking;
    const bookingStartMoment = moment(bookingStart).tz('Europe/London');
    const bookingDate = bookingStartMoment.format('YYYY-MM-DD HH:mm');

    if (!groupedTransactions[bookingDate]) {
      // eslint-disable-next-line no-param-reassign
      groupedTransactions[bookingDate] = {
        seats,
      };
    } else {
      // eslint-disable-next-line no-param-reassign
      groupedTransactions[bookingDate].seats += seats;
    }
    return groupedTransactions;
  }, {});
};

const fetchAllOwnTransactions = async ({ listing, sdk, accumulatedTransactions, page }) => {
  const accumulatedTransactionsSafe = accumulatedTransactions ?? [];
  const currentPage = page ?? 1;
  const transactionsResponse = await sdk.transactions.query({
    listingId: listing.id.uuid,
    states: 'state/accepted,state/delivered,state/preauthorized,state/reviewed',
    sort: 'bookingStart',
    include: ['booking'],
    page: currentPage,
  });
  const {
    data: { meta },
  } = transactionsResponse;

  const hasMorePages = meta?.totalPages > currentPage;
  const transactions = denormalisedResponseEntities(transactionsResponse);

  if (hasMorePages) {
    return fetchAllOwnTransactions({
      listing,
      sdk,
      accumulatedTransactions: accumulatedTransactionsSafe.concat(transactions),
      page: currentPage + 1,
    });
  }

  return accumulatedTransactionsSafe.concat(transactions);
};

const fetchOwnTransactions =
  ({ listing }) =>
  async (dispatch, getState, sdk) => {
    try {
      dispatch(fetchOwnTransactionsRequest());
      const transactions = await fetchAllOwnTransactions({ listing, sdk });
      const groupedTransactionsByDate = groupTransactionsByDate(transactions);
      dispatch(fetchOwnTransactionsSuccess(groupedTransactionsByDate));
      return groupedTransactionsByDate;
    } catch (e) {
      dispatch(fetchOwnTransactionsError(storableError(e)));
      return {};
    }
  };

export const showListing =
  (listingId, config, isOwn = false) =>
  (dispatch, getState, sdk) => {
    const {
      aspectWidth = 1,
      aspectHeight = 1,
      variantPrefix = 'listing-card',
    } = config.layout.listingImage;
    const aspectRatio = aspectHeight / aspectWidth;

    dispatch(showListingRequest(listingId));
    // Current user entity is fetched in a bit lazy fashion, since it's not tied to returned Promise chain.
    const fetchCurrentUserOptions = {
      updateHasListings: false,
      updateNotifications: false,
    };
    dispatch(fetchCurrentUser(fetchCurrentUserOptions));

    const params = {
      id: listingId,
      include: ['author', 'author.profileImage', 'images', 'currentStock'],
      'fields.image': [
        // Scaled variants for large images
        'variants.scaled-small',
        'variants.scaled-medium',
        'variants.scaled-large',
        'variants.scaled-xlarge',

        // Cropped variants for listing thumbnail images
        `variants.${variantPrefix}`,
        `variants.${variantPrefix}-2x`,
        `variants.${variantPrefix}-4x`,
        `variants.${variantPrefix}-6x`,

        // Social media
        'variants.facebook',
        'variants.twitter',

        // Avatars
        'variants.square-small',
        'variants.square-small2x',
      ],
      ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
      ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
      ...createImageVariantConfig(`${variantPrefix}-4x`, 1600, aspectRatio),
      ...createImageVariantConfig(`${variantPrefix}-6x`, 2400, aspectRatio),
    };

    // Own listings (drafts, closed) are read through v2 so a manager can preview them too.
    const show = isOwn ? listingsApi.ownListings.show(params) : sdk.listings.show(params);

    return show
      .then((data) => {
        const listings = denormalisedResponseEntities(data);
        const isOwnListing = listings[0].author.id.uuid === getState().user?.currentUser?.id?.uuid;

        if (isOwnListing) {
          dispatch(fetchOwnTransactions({ listing: listings[0] }));
        }

        const listingFields = config?.listing?.listingFields;
        const sanitizeConfig = { listingFields };
        dispatch(addMarketplaceEntities(data, sanitizeConfig));
        return data;
      })
      .catch((e) => {
        dispatch(showListingError(storableError(e)));
      });
  };

export const fetchReviews =
  ({ subjectId }) =>
  (dispatch, getState, sdk) => {
    dispatch(fetchReviewsRequest());
    return sdk.reviews
      .query({
        subjectId,
        state: 'public',
        include: ['author', 'author.profileImage'],
        'fields.image': ['variants.square-small', 'variants.square-small2x'],
      })
      .then((response) => {
        const reviews = denormalisedResponseEntities(response);
        dispatch(fetchReviewsSuccess(reviews));
      })
      .catch((e) => {
        dispatch(fetchReviewsError(storableError(e)));
      });
  };

const timeSlotsRequest = (params) => (dispatch, getState, sdk) => {
  const currentState = getState();
  const currentListings = getMarketplaceEntities(currentState, [
    {
      id: params.listingId,
      type: 'listing',
    },
  ]);
  const currentOwnListings = getMarketplaceEntities(currentState, [
    {
      id: params.listingId,
      type: 'ownListing',
    },
  ]);

  const currentListing = currentListings[0] || currentOwnListings[0];

  return sdk.timeslots.query(params).then((response) => {
    const denormalisedResponses = denormalisedResponseEntities(response);
    return splitMergedTimeSlot({
      availabilityPlan: currentListing?.attributes?.publicData?.availabilityPlan,
      timeSlots: denormalisedResponses,
    });
  });
};

export const fetchTimeSlots =
  (listingId, start, end, timeZone, options) => async (dispatch, getState, sdk) => {
    const { extraQueryParams = null, useFetchTimeSlotsForDate = false } = options || {};

    // The maximum pagination page size for timeSlots is 500
    const extraParams = extraQueryParams || {
      perPage: 500,
      page: 1,
    };

    // For small time units, we fetch the data per date.
    // This is to avoid fetching too much data (with 15 minute intervals, there can be 24*4*31 = 2928 time slots)
    if (useFetchTimeSlotsForDate) {
      const dateId = stringifyDateToISO8601(start, timeZone);
      const dateData = getState().ListingPage.timeSlotsForDate[dateId];
      const minuteAgo = new Date().getTime() - MINUTE_IN_MS;
      const hasRecentlyFetchedData = dateData?.fetchedAt > minuteAgo;
      if (hasRecentlyFetchedData) {
        return Promise.resolve(dateData?.timeSlots || []);
      }

      dispatch(fetchTimeSlotsForDateRequest(dateId));

      try {
        const timeSlots = await dispatch(
          timeSlotsRequest({ listingId, start, end, ...extraParams })
        );
        dispatch(fetchTimeSlotsForDateSuccess(dateId, timeSlots));
        return timeSlots;
      } catch (e) {
        dispatch(fetchTimeSlotsForDateError(dateId, storableError(e)));
        return [];
      }
    } else {
      const monthId = monthIdString(start, timeZone);
      dispatch(fetchMonthlyTimeSlotsRequest(monthId));

      try {
        const timeSlots = await dispatch(
          timeSlotsRequest({ listingId, start, end, ...extraParams })
        );

        // Split slots by their actual month so each monthId key contains
        // only the slots that belong to that calendar month. This is
        // necessary because a single query can span multiple months
        // (e.g. 90-day chunks), and consumers like checkSessionCapacity
        // look up slots by individual month keys.
        const slotsByMonth = {};
        for (const slot of timeSlots) {
          const slotMonthId = monthIdString(slot.attributes.start, timeZone);
          if (!slotsByMonth[slotMonthId]) slotsByMonth[slotMonthId] = [];
          slotsByMonth[slotMonthId].push(slot);
        }

        for (const [mid, monthSlots] of Object.entries(slotsByMonth)) {
          // Merge with any slots already stored for this month rather than
          // overwriting — when a 90-day chunk boundary falls mid-month, both
          // chunks return slots for that month and the second must not discard
          // what the first already stored.
          const existingSlots = getState().ListingPage.monthlyTimeSlots[mid]?.timeSlots || [];
          const existingIds = new Set(existingSlots.map((s) => s.id?.uuid).filter(Boolean));
          const mergedSlots = [
            ...existingSlots,
            ...monthSlots.filter((s) => !existingIds.has(s.id?.uuid)),
          ];
          dispatch(fetchMonthlyTimeSlotsSuccess(mid, mergedSlots));
        }

        // If this chunk returned no slots for its month, preserve any data
        // a previous chunk already stored rather than overwriting with empty.
        if (!slotsByMonth[monthId]) {
          const existing = getState().ListingPage.monthlyTimeSlots[monthId];
          dispatch(fetchMonthlyTimeSlotsSuccess(monthId, existing?.timeSlots || []));
        }

        return timeSlots;
      } catch (e) {
        dispatch(fetchMonthlyTimeSlotsError(monthId, storableError(e)));
        return [];
      }
    }
  };

export const sendInquiry = (listing, message) => (dispatch, getState, sdk) => {
  dispatch(sendInquiryRequest());
  const { listingType, transactionProcessAlias: processAlias } =
    listing?.attributes?.publicData || {};
  if (!processAlias) {
    const error = new Error('No transaction process attached to listing');
    log.error(error, 'listing-process-missing', {
      listingId: listing?.id?.uuid,
    });
    dispatch(sendInquiryError(storableError(error)));
    return Promise.reject(error);
  }

  const listingId = listing?.id;

  const [processName] = processAlias.split('/');
  const transitions = getProcess(processName)?.transitions;

  const bodyParams = {
    transition: transitions.INQUIRE,
    processAlias: listingType === BOOKING_CLASS_LISTING ? BLOCK_BOOKING_ALIAS : processAlias,
    params: { listingId },
  };
  return sdk.transactions
    .initiate(bodyParams)
    .then((response) => {
      const transactionId = response.data.data.id;

      // Send the message to the created transaction
      return sdk.messages.send({ transactionId, content: message }).then(() => {
        dispatch(sendInquirySuccess());
        dispatch(fetchCurrentUserHasOrdersSuccess(true));
        return transactionId;
      });
    })
    .catch((e) => {
      dispatch(sendInquiryError(storableError(e)));
      throw e;
    });
};

// Helper function for loadData call.
// Note: listing could be ownListing entity too
const fetchMonthlyTimeSlots = async (dispatch, listing) => {
  const hasWindow = typeof window !== 'undefined';
  const { availabilityPlan, publicData } = listing?.attributes || {};
  const tz = availabilityPlan?.timezone;
  const {
    startTime,
    endTime,
    availabilityPlan: defaultAvailabilityPlan,
    recurringOption,
    isRecurring,
    endDate,
    endOption,
  } = publicData || {};

  const sessionLengthInMinutes =
    recurringOption === CUSTOM_SCHEDULE && isRecurring === 'yes'
      ? defaultAvailabilityPlan.entries.reduce((sessionLength, plan) => {
          const { startTime: planStartTime, endTime: planEndTime } = plan ?? {};
          if (planStartTime && planEndTime) {
            const start = moment(planStartTime, 'HH:mm');
            const end = moment(planEndTime, 'HH:mm');
            return Math.min(sessionLength, end.diff(start, 'minutes'));
          }
          return sessionLength;
        }, 120)
      : moment(endTime, 'HH:mm').diff(moment(startTime, 'HH:mm'), 'minutes');

  // Fetch time-zones on client side only.
  if (hasWindow && listing.id && !!tz) {
    const { unitType } = publicData || {};
    const now = new Date();
    const startOfToday = getStartOf(now, 'day', tz);
    const isFixed = unitType === 'fixed';

    const timeUnit = 'oneMinute';
    const nextBoundary = findNextBoundary(now, 1, timeUnit, tz);

    // Adjust to query in 90-day chunks due to API constraints
    const bookingLengthInMinutes = sessionLengthInMinutes;

    const minDurationStartingInInterval = isFixed ? bookingLengthInMinutes : 60;

    const options = (intervalAlign) => {
      return ['fixed', 'hour'].includes(unitType)
        ? {
            extraQueryParams: {
              intervalDuration: 'P1D',
              intervalAlign,
              maxPerInterval: 100,
              minDurationStartingInInterval,
              perPage: MAX_DAYS_PER_QUERY,
              page: 1,
            },
          }
        : null;
    };

    const timeSlots = [];
    let currentStart = nextBoundary;
    let maxEnd = getStartOf(nextBoundary, 'day', tz, MAX_TOTAL_DAYS, 'days');

    // Determine availability end: prefer explicit endDate (when set as specific
    // end option), otherwise try to compute an end date from session count/config
    // using `getEndDate`. This ensures listings that end after N sessions are
    // respected and we don't fetch months beyond their availability.
    const computedEnd = getEndDate(publicData);
    const explicitEnd = endOption === END_OPTION_SPECIFIC_DATE && endDate ? Number(endDate) : null;
    const availabilityEnd = explicitEnd ?? computedEnd;

    if (availabilityEnd) {
      try {
        const availabilityEndDate = new Date(Number(availabilityEnd));
        const availabilityEndExclusive = getStartOf(availabilityEndDate, 'day', tz, 1, 'days');
        if (availabilityEndExclusive && availabilityEndExclusive < maxEnd) {
          maxEnd = availabilityEndExclusive;
        }
      } catch (err) {
        // If parsing fails, use default maxEnd
      }
    }

    while (currentStart < maxEnd) {
      const currentEnd = getStartOf(currentStart, 'day', tz, MAX_DAYS_PER_QUERY, 'days');
      const adjustedEnd = isFixed
        ? getStartOf(currentEnd, 'minute', tz, bookingLengthInMinutes, 'minutes')
        : currentEnd;
      const constrainedEnd = adjustedEnd > maxEnd ? maxEnd : adjustedEnd;
      const slots = await dispatch(
        fetchTimeSlots(listing.id, currentStart, constrainedEnd, tz, options(currentStart))
      );
      timeSlots.push(slots);
      currentStart = constrainedEnd;
    }

    dispatch(fetchTimeSlotsSuccess());
    return timeSlots;
  }

  // By default return an empty array
  return Promise.all([]);
};

export const fetchTransactionLineItems =
  ({ orderData, listingId, isOwnListing, isFreePromoCode, freePromoCode }) =>
  (dispatch) => {
    dispatch(fetchLineItemsRequest());

    transactionLineItems({ orderData, listingId, isOwnListing, isFreePromoCode, freePromoCode })
      .then((response) => {
        const lineItems = response.data;
        dispatch(fetchLineItemsSuccess(lineItems));
      })
      .catch((e) => {
        dispatch(fetchLineItemsError(storableError(e)));
        // Capture the server-provided status/message (surfaced by the axios
        // interceptor in util/api.js) so this client breadcrumb correlates
        // with the server-side `transaction-line-items-failed` log.
        log.error(e, 'fetching-line-items-failed', {
          listingId: listingId.uuid,
          isOwnListing,
          isFreePromoCode,
          orderData,
          errorStatus: e?.status,
          errorMessage: e?.message,
        });
      });
  };

const createListOfMonths = (bookingStart, bookingEnd) => {
  const months = [];
  let current = moment(bookingStart).startOf('month');
  const last = moment(bookingEnd).startOf('month');

  while (current.isSameOrBefore(last)) {
    months.push(current.format('YYYY-MM'));
    current = current.add(1, 'month');
  }
  return months;
};

const calculateRemovedSessions = ({ exceptions, sessions }) => {
  if (!exceptions) {
    return 0;
  }

  const hasPrefixBlockedSessionsThatBlockTillStartDate = exceptions.some((exception) => {
    const { start } = exception;
    const startDate = moment(start);
    return startDate.isBefore(moment(sessions[0].rawDate));
  });
  const hasSuffixBlockedSessionsThatBlockAfterEndDate = exceptions.some((exception) => {
    const { end } = exception;
    const endDate = moment(end);
    return endDate.isAfter(moment(sessions[sessions.length - 1].rawDate));
  });

  let subtractCount = 0;

  if (hasPrefixBlockedSessionsThatBlockTillStartDate) {
    subtractCount += 1;
  }

  if (hasSuffixBlockedSessionsThatBlockAfterEndDate) {
    subtractCount += 1;
  }

  return exceptions.length - subtractCount;
};
// Need to refactor this function to make it shorter and handle better cases with multiple booking checks

const checkSessionCapacity = ({
  seats,
  bookingStart,
  bookingEnd,
  availability,
  listing,
  firstDayOfWeek,
  ticketOption,
  needToRecalculateBookingEnd,
  blockId,
}) => {
  const {
    isRecurring,
    firstSession,
    recurringOption,
    numberOfSessions,
    endDate,
    endOption = 'after-sessions',
    availabilityPlan,
    exceptions,
    startTime,
    endTime,
    blockBooking,
  } = listing?.attributes?.publicData ?? {};

  if (blockId) {
    const currentWeekEntry = listing.attributes.publicData.weekEntries.find(({ id }) => {
      return id === blockId;
    });
    const allSessions = getSession({
      isRecurring,
      firstSession,
      recurringOption,
      numberOfSessions,
      endDate,
      endOption,
      availabilityPlan,
      firstDayOfWeek,
      exceptions,
      startTime,
      endTime,
      blockBooking,
    });
    const canIgnorePastDay = listing.attributes.publicData.blockBooking?.isAllowPartway === 'yes';
    const eligibleSessions = allSessions.reduce((acc, session) => {
      const { allSessions: allDaySessions, rawDate } = session;
      if (!Array.isArray(allDaySessions) || !currentWeekEntry) {
        return acc;
      }
      const currentDayOfWeek = rawDate.tz('Europe/London').format('ddd').toLowerCase();

      const availableSubSession = allDaySessions.find((subSession) => {
        const dayEntry = currentWeekEntry?.[currentDayOfWeek]?.[0];
        if (!dayEntry) {
          return false;
        }
        const { startTime: entryStartTime, endTime: entryEndTime } = dayEntry;
        if (
          moment().isAfter(
            rawDate.clone().set({
              hour: parseInt(entryStartTime.split(':')[0], 10),
              minute: parseInt(entryStartTime.split(':')[1], 10),
            })
          )
        ) {
          if (!canIgnorePastDay) {
            // eslint-disable-next-line no-throw-literal
            throw {
              data: {
                errors: [
                  {
                    title: `Booking does not allow joining part way.`,
                  },
                ],
              },
            };
          }
          return false;
        }
        return entryStartTime === subSession.startTime && entryEndTime === subSession.endTime;
      });

      if (availableSubSession) {
        acc.push(availableSubSession);
      }

      return acc;
    }, []);

    const bookingEndToCalculate = eligibleSessions[eligibleSessions.length - 1].rawDate;

    const listOfMonths = createListOfMonths(bookingStart, bookingEndToCalculate);

    const isNotEnoughSeat = listOfMonths.reduce((isNotEnoughSeatAcc, monthId) => {
      if (isNotEnoughSeatAcc) {
        return isNotEnoughSeatAcc; // If already found not enough seats, skip further checks
      }

      const monthlyTimeSlots = availability[monthId];
      if (Array.isArray(monthlyTimeSlots?.timeSlots)) {
        // eslint-disable-next-line no-restricted-syntax
        for (const timeSlot of monthlyTimeSlots.timeSlots) {
          const { start: timeSlotStart, end: timeSlotEnd } = timeSlot.attributes;
          const momentTimeSlotStart = moment(timeSlotStart).tz('Europe/London');
          const momentTimeSlotEnd = moment(timeSlotEnd).tz('Europe/London');
          const currentTimeSlotDayOfWeek = momentTimeSlotStart.format('ddd').toLowerCase();
          const { startTime: entryStartTime, endTime: entryEndTime } =
            currentWeekEntry?.[currentTimeSlotDayOfWeek]?.[0] || {};

          if (
            bookingEnd.getTime() <= timeSlot.attributes.end.getTime() &&
            entryStartTime === momentTimeSlotStart.format('HH:mm') &&
            entryEndTime === momentTimeSlotEnd.format('HH:mm')
          ) {
            // eslint-disable-next-line no-shadow
            const { seats: availableSeats = 0 } = timeSlot.attributes;
            if (availableSeats < seats) {
              return true;
            }
          }
        }
      }
      return isNotEnoughSeatAcc;
    }, false);

    return isNotEnoughSeat;
  }

  let sessions = needToRecalculateBookingEnd
    ? (getSession({
        isRecurring,
        firstSession,
        recurringOption,
        numberOfSessions,
        endDate,
        endOption,
        availabilityPlan,
        firstDayOfWeek,
        exceptions,
        startTime,
        endTime,
        blockBooking,
      }) ?? [])
    : null;

  const bookingEndToCalculate = needToRecalculateBookingEnd
    ? sessions[sessions.length - 1].rawDate
    : bookingEnd;

  const listOfMonths = createListOfMonths(bookingStart, bookingEndToCalculate);

  let sharetribeCountedSessions = 0;

  if (ticketOption === 'payAsYouGo' || ticketOption === 'freeTrial') {
    const isNotEnoughSeat = listOfMonths.reduce((isNotEnoughSeatAcc, monthId) => {
      if (isNotEnoughSeatAcc) {
        return isNotEnoughSeatAcc; // If already found not enough seats, skip further checks
      }

      const monthlyTimeSlots = availability[monthId];
      if (Array.isArray(monthlyTimeSlots?.timeSlots)) {
        sharetribeCountedSessions += monthlyTimeSlots.timeSlots.length;
        // eslint-disable-next-line no-restricted-syntax
        for (const timeSlot of monthlyTimeSlots.timeSlots) {
          if (bookingEnd.getTime() === timeSlot.attributes.end.getTime()) {
            // eslint-disable-next-line no-shadow
            const { seats: availableSeats = 0 } = timeSlot.attributes;
            if (availableSeats < seats) {
              return true;
            }
          }
        }
      }
      return isNotEnoughSeatAcc;
    }, false);
    return isNotEnoughSeat;
  }

  const isNotEnoughSeat = listOfMonths.reduce((isNotEnoughSeatAcc, monthId) => {
    if (isNotEnoughSeatAcc) {
      return isNotEnoughSeatAcc; // If already found not enough seats, skip further checks
    }

    const monthlyTimeSlots = availability[monthId];
    if (Array.isArray(monthlyTimeSlots?.timeSlots)) {
      sharetribeCountedSessions += monthlyTimeSlots.timeSlots.length;
      // eslint-disable-next-line no-restricted-syntax
      for (const timeSlot of monthlyTimeSlots.timeSlots) {
        if (bookingEnd.getTime() <= timeSlot.attributes.end.getTime()) {
          // eslint-disable-next-line no-shadow
          const { seats: availableSeats = 0 } = timeSlot.attributes;
          if (availableSeats < seats) {
            return true;
          }
        }
      }
    }
    return isNotEnoughSeatAcc;
  }, false);

  if (isNotEnoughSeat) {
    return true; // If already found not enough seats, return true
  }

  sessions =
    sessions ??
    getSession({
      isRecurring,
      firstSession,
      recurringOption,
      numberOfSessions,
      endDate,
      endOption,
      availabilityPlan,
      firstDayOfWeek,
      exceptions,
      startTime,
      endTime,
      blockBooking,
    }) ??
    [];

  const canIgnorePastDay = listing.attributes.publicData.blockBooking?.isAllowPartway === 'yes';
  const sessionsHasAPastDay = sessions.find((session) => session.past);

  if (!canIgnorePastDay && sessionsHasAPastDay) {
    // eslint-disable-next-line no-throw-literal
    throw {
      data: {
        errors: [
          {
            title: `Booking does not allow joining part way.`,
          },
        ],
      },
    };
  }

  sessions = canIgnorePastDay ? sessions.filter((session) => !session.past) : sessions;

  const needToConsiderException = recurringOption !== 'custom-schedule' && isRecurring === 'yes';

  const removedSessionCount = needToConsiderException
    ? calculateRemovedSessions({
        exceptions,
        sessions,
      })
    : 0;

  const countedSessions = sharetribeCountedSessions + removedSessionCount;

  if (countedSessions < sessions.length) {
    log.error(
      new Error('Session count mismatch between listing and Sharetribe availability'),
      'session-count-mismatch',
      {
        listingId: listing?.id?.uuid,
        countedSessions,
        expectedSessions: sessions.length,
      }
    );
    // eslint-disable-next-line no-throw-literal
    throw {
      data: {
        errors: [
          {
            title: 'Session count mismatch. Please contact support.',
          },
        ],
      },
    };
  }

  return isNotEnoughSeat;
};

export const tryInitiateTransaction = (params) => async (dispatch, getState) => {
  dispatch(speculateTransactionRequest());

  try {
    const { listing, orderData } = params;
    const { ticketOption } = orderData;

    const availability = getState().ListingPage.monthlyTimeSlots;

    const isCustomScheduleBooking =
      listing.attributes?.publicData?.recurringOption === CUSTOM_SCHEDULE &&
      listing.attributes?.publicData?.isRecurring === 'yes';

    // Have not handle the cases where the block booking does not have enough slots in one sub booking

    if (
      (isCustomScheduleBooking && !ticketOption?.includes('blockBooking')) ||
      ticketOption === 'freeTrial' ||
      ticketOption === 'payAsYouGo'
    ) {
      const { sessions } = orderData ?? {};
      if (!sessions || !Array.isArray(sessions) || sessions.length === 0) {
        // eslint-disable-next-line no-throw-literal
        throw {
          data: {
            errors: [
              {
                title: `How can you book a session without selecting any? Please select at least one session.`,
              },
            ],
          },
        };
      }
      sessions.forEach((session) => {
        const { bookingStartTime, bookingEndTime, adultSeats, childSeats, siblingSeats } = session;

        const safeChildSeats = Number.isNaN(Number(childSeats)) ? 0 : parseInt(childSeats, 10);
        const safeAdultSeats = Number.isNaN(Number(adultSeats)) ? 0 : parseInt(adultSeats, 10);
        const safeSiblingSeats = Number.isNaN(Number(siblingSeats))
          ? 0
          : parseInt(siblingSeats, 10);

        const totalSeats = safeChildSeats + safeAdultSeats + safeSiblingSeats;

        const isNotEnoughSeat = checkSessionCapacity({
          seats: totalSeats,
          bookingStart: new Date(parseInt(bookingStartTime, 10)),
          bookingEnd: new Date(parseInt(bookingEndTime, 10)),
          availability,
          listing,
          firstDayOfWeek: 1,
          ticketOption,
        });

        if (isNotEnoughSeat) {
          // eslint-disable-next-line no-throw-literal
          throw {
            data: {
              errors: [
                {
                  title: `The requested booking time is not available.`,
                },
              ],
            },
          };
        }
      });
    } else {
      const needToRecalculateBookingEnd = ticketOption?.includes('blockBooking');
      const hasMultipleBlocks = Array.isArray(orderData?.blocks);

      if (!hasMultipleBlocks) {
        const {
          bookingDates: { bookingStart, bookingEnd },
          adultSeats,
          childSeats,
          siblingSeats,
        } = orderData ?? {};

        const safeChildSeats = Number.isNaN(Number(childSeats)) ? 0 : parseInt(childSeats, 10);
        const safeAdultSeats = Number.isNaN(Number(adultSeats)) ? 0 : parseInt(adultSeats, 10);
        const safeSiblingSeats = Number.isNaN(Number(siblingSeats))
          ? 0
          : parseInt(siblingSeats, 10);

        const totalSeats = safeChildSeats + safeAdultSeats + safeSiblingSeats;

        // TODO: Fetch configuration from marketplace config
        const isNotEnoughSeat = checkSessionCapacity({
          seats: totalSeats,
          bookingStart,
          bookingEnd,
          availability,
          listing,
          firstDayOfWeek: 1,
          ticketOption,
          needToRecalculateBookingEnd,
        });

        if (isNotEnoughSeat) {
          // eslint-disable-next-line no-throw-literal
          throw {
            data: {
              errors: [
                {
                  title: `The requested booking time is not available.`,
                },
              ],
            },
          };
        }
      }

      // eslint-disable-next-line no-restricted-syntax
      for (const block of orderData?.blocks || []) {
        const {
          blockTicketOption,
          bookingStartTime,
          bookingEndTime,
          adultSeats,
          childSeats,
          siblingSeats,
        } = block;
        const safeChildSeats = Number.isNaN(Number(childSeats)) ? 0 : parseInt(childSeats, 10);
        const safeAdultSeats = Number.isNaN(Number(adultSeats)) ? 0 : parseInt(adultSeats, 10);
        const safeSiblingSeats = Number.isNaN(Number(siblingSeats))
          ? 0
          : parseInt(siblingSeats, 10);

        const totalSeats = safeChildSeats + safeAdultSeats + safeSiblingSeats;

        const isNotEnoughSeat = checkSessionCapacity({
          seats: totalSeats,
          bookingStart: new Date(parseInt(bookingStartTime, 10)),
          bookingEnd: new Date(parseInt(bookingEndTime, 10)),
          availability,
          listing,
          firstDayOfWeek: 1,
          ticketOption,
          needToRecalculateBookingEnd,
          blockId: blockTicketOption,
        });

        if (isNotEnoughSeat) {
          // eslint-disable-next-line no-throw-literal
          throw {
            data: {
              errors: [
                {
                  title: `The requested booking time is not available.`,
                },
              ],
            },
          };
        }
      }
    }

    const processAlias = determineTxProcessAlias({
      ticketOption,
      processAlias: listing?.attributes?.publicData?.transactionProcessAlias,
      isFreeClass: isFreeListing(listing),
      isFreeBlockBooking: isFreeBlockBookingListing(listing),
      isFreePromoCode: orderData?.isFreePromoCode,
    });
    const processName = processAlias?.split('/')?.[0];
    const transactionProcess = getProcess(processName);
    const requestTransition = transactionProcess?.getRequestTransition?.({});

    if (!requestTransition) {
      throw new Error(
        `Transaction process "${processName}" does not have a request transition defined.`
      );
    }

    const { deliveryMethod, quantity, bookingDates, ...otherOrderParams } = orderData;
    const quantityMaybe = quantity ? { stockReservationQuantity: quantity } : {};
    const bookingParamsMaybe = bookingDates || {};

    // Parameters for Marketplace API
    const transitionParams = {
      listingId: listing.id.uuid,
      ...quantityMaybe,
      ...bookingParamsMaybe,
      ...otherOrderParams,
    };

    const bodyParams = {
      processAlias,
      transition: requestTransition,
      params: transitionParams,
    };
    await speculateTransaction({
      bodyParams,
    });
    dispatch(speculateTransactionSuccess());
  } catch (e) {
    dispatch(speculateTransactionError(storableError(e)));
    throw e; // Re-throw the error to be handled by the caller
  }
};

export const loadData = (params, search, config) => async (dispatch, getState, sdk) => {
  const { slug } = params;
  try {
    dispatch(clearWaitlistState());
    // Clear any previous redirect
    dispatch(setRedirectSlug(null));

    const slugIdResponse = await getIdBySlug(slug);

    // Check if this slug is a redirect to another slug
    if (slugIdResponse.data.isRedirect && slugIdResponse.data.headSlug) {
      dispatch(setRedirectSlug(slugIdResponse.data.headSlug));
      return;
    }

    const listingId = new UUID(slugIdResponse.data.id);

    const state = getState();
    const currentUser = state.user?.currentUser;
    const inquiryModalOpenForListingId =
      isUserAuthorized(currentUser) && hasPermissionToInitiateTransactions(currentUser)
        ? state.ListingPage.inquiryModalOpenForListingId
        : null;

    // Clear old line-items
    dispatch(setInitialValues({ lineItems: null, inquiryModalOpenForListingId }));

    const ownListingVariants = [LISTING_PAGE_DRAFT_VARIANT, LISTING_PAGE_PENDING_APPROVAL_VARIANT];
    if (ownListingVariants.includes(params.variant)) {
      return dispatch(showListing(listingId, config, true));
    }

    // In private marketplace mode, this page won't fetch data if the user is unauthorized
    const isAuthorized = currentUser && isUserAuthorized(currentUser);
    const isPrivateMarketplace = config.accessControl.marketplace.private === true;
    const canFetchData = !isPrivateMarketplace || (isPrivateMarketplace && isAuthorized);
    if (!canFetchData) {
      return Promise.resolve();
    }

    const hasNoViewingRights = currentUser && !hasPermissionToViewData(currentUser);
    const listingResponse = await dispatch(showListing(listingId, config, hasNoViewingRights));
    const listing = listingResponse?.data?.data;
    const transactionProcessAlias = listing?.attributes?.publicData?.transactionProcessAlias || '';

    const { isPrivate, hasPasscode } = listing?.attributes.publicData || {};
    const isOwnListing = listing.relationships.author.data.id.uuid === currentUser?.id.uuid;
    const shouldCheckPasscode = isPrivate && hasPasscode && !isOwnListing;
    if (isBookingProcessAlias(transactionProcessAlias) && !hasNoViewingRights) {
      // Fetch timeSlots if the user has viewing rights.
      // This can happen parallel to loadData.
      // We are not interested to return them from loadData call.
      fetchMonthlyTimeSlots(dispatch, listing);
    }

    let reviewsResponse;
    if (!hasNoViewingRights) {
      reviewsResponse = await dispatch(
        fetchReviews({ subjectId: listing.relationships.author.data.id.uuid })
      );
    }

    if (shouldCheckPasscode) {
      return dispatch(initPasscodeValidationState());
    }

    return hasNoViewingRights ? [listingResponse] : [listingResponse, reviewsResponse];
  } catch (e) {
    const error = storableError(e);
    log.error(error, 'listing-page-load-data-failed', {
      slug,
      error: error.message,
    });
    return Promise.reject(error);
  }
};

export const makeListingLive = (listingId) => async (dispatch, getState, sdk) => {
  dispatch(makeListingLiveRequest());
  try {
    await sdk.ownListings.update(
      { id: listingId, publicData: { isPrivate: false, awaitingStripeConnect: false } },
      { expand: true }
    );
    await updateUserProfile({ publicData: { onboardingPosition: 'personalise-profile' } });
    window.posthog?.capture?.('onboarding transition personalise-profile');
    dispatch(makeListingLiveSuccess());
    window.location.href = '/organisation';
  } catch (e) {
    log.error(e, 'make-listing-live-failed', { listingId });
    dispatch(makeListingLiveError(storableError(e)));
  }
};

export const selectListing = (state) => (ref) => {
  const listings = getMarketplaceEntities(state, [ref]);
  return listings?.[0] ?? null;
};
